/**
 * Public Layout Component
 *
 * Shared layout for all public/unauthenticated pages with consistent
 * header navigation and footer.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { LanguageSelector } from '../ui/LanguageSelector';

interface PublicLayoutProps {
  children: React.ReactNode;
  /** Background class for the main content area */
  bgClass?: string;
  /** Whether to show the full navigation or a simplified one */
  minimal?: boolean;
}

export default function PublicLayout({ children, bgClass = 'bg-white', minimal = false }: PublicLayoutProps) {
  const { t } = useTranslation('landing');
  const { isAuthenticated } = useAuthStore();

  return (
    <div className={`min-h-screen flex flex-col ${bgClass}`}>
      {/* Navigation */}
      <nav className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-xl font-bold text-gray-900">xtFORM</span>
            </Link>

            {/* Navigation Links */}
            {!minimal && (
              <div className="hidden md:flex items-center gap-6">
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                  {t('nav.features')}
                </Link>
                {/* Solutions Dropdown */}
                <div className="relative group">
                  <button className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-1">
                    {t('nav.solutions', 'Solutions')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <Link to="/education" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
                      <div className="font-medium">{t('nav.education', 'Education')}</div>
                      <div className="text-xs text-gray-500">{t('nav.educationDesc', 'For teachers & schools')}</div>
                    </Link>
                    <Link to="/business" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50">
                      <div className="font-medium">{t('nav.business', 'Business')}</div>
                      <div className="text-xs text-gray-500">{t('nav.businessDesc', 'For teams & enterprises')}</div>
                    </Link>
                    <Link to="/events" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg">
                      <div className="font-medium">{t('nav.events', 'Events')}</div>
                      <div className="text-xs text-gray-500">{t('nav.eventsDesc', 'For conferences & meetups')}</div>
                    </Link>
                  </div>
                </div>
                <Link to="/blog" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                  {t('nav.blog', 'Blog')}
                </Link>
                <Link to="/#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                  {t('nav.pricing')}
                </Link>
              </div>
            )}

            {/* Right side */}
            <div className="flex items-center gap-4">
              <LanguageSelector variant="compact" />
              {isAuthenticated ? (
                <Link
                  to="/formspace"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
                >
                  {t('nav.goToDashboard')}
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm font-medium hidden sm:block">
                    {t('nav.signIn')}
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
                  >
                    {t('nav.getStartedFree')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xl font-bold text-white">xtFORM</span>
              </div>
              <p className="text-sm">
                {t('footer.tagline')}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register" className="hover:text-white transition-colors">{t('footer.getStarted')}</Link></li>
                <li><Link to="/#features" className="hover:text-white transition-colors">{t('footer.features')}</Link></li>
                <li><Link to="/#live-quiz" className="hover:text-white transition-colors">{t('footer.liveQuiz')}</Link></li>
                <li><Link to="/docs/api" className="hover:text-white transition-colors">{t('footer.apiDocs')}</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">{t('nav.blog', 'Blog')}</Link></li>
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('nav.solutions', 'Solutions')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/education" className="hover:text-white transition-colors">{t('nav.education', 'Education')}</Link></li>
                <li><Link to="/business" className="hover:text-white transition-colors">{t('nav.business', 'Business')}</Link></li>
                <li><Link to="/events" className="hover:text-white transition-colors">{t('nav.events', 'Events')}</Link></li>
                <li><Link to="/demo/quiz" className="hover:text-white transition-colors">{t('nav.demoQuiz', 'Demo Quiz')}</Link></li>
                <li><Link to="/demo/poll" className="hover:text-white transition-colors">{t('nav.demoPoll', 'Demo Poll')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">{t('footer.cookies')}</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">{t('footer.about', 'About Us')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
